import { fetchUserInfo, updateUserInfo } from "actions/userActions"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { getCompanyInfo } from "reducers/companyInfo"
import { getUserInfo } from "reducers/userInfo"
import { getBenefitStarted } from "../../../derivedSelectors"

const UserContext = React.createContext(null)

type RegistrationCohortHelper =
  | "showRegPostCarrotPlansSoftLaunchFeatures"
  | "showRegPostCarrotPlansRoutingFlowFeatures"
  | "showRegPreCarrotPlansSoftLaunchFeatures"
  | "showRegPreCarrotPlansRoutingFlowFeatures"

export const useCurrentUser = () => React.useContext(UserContext)

export const REGISTRATION_COHORTS = {
  ONE: 1,
  TWO: 2,
  THREE: 3
}

/*
  We set what cohort a member should be in when registering below. This is set in the frontend and sent to the backend to
  be saved on the Employee record because these cohorts change based on code release. And we toggle features based on
  what cohort the member registers in. This is currently only used for Carrot Plans.
*/
export const currentlySetRegistrationCohort = REGISTRATION_COHORTS.THREE

export function UserContextProvider({ children }): JSX.Element {
  const dispatch = useDispatch()
  const userInfo = useSelector(getUserInfo)
  const company = useSelector(getCompanyInfo)
  const benefitStarted = useSelector(getBenefitStarted)

  function refreshUserInfo() {
    return dispatch(fetchUserInfo())
  }

  const getFirstName = React.useCallback((): string => userInfo?.preferredName || userInfo?.firstName, [
    userInfo?.firstName,
    userInfo?.preferredName
  ])

  function getRegistrationCohort(): Record<RegistrationCohortHelper, boolean> {
    const cohort = {
      showRegPostCarrotPlansSoftLaunchFeatures: false,
      showRegPostCarrotPlansRoutingFlowFeatures: false,
      showRegPreCarrotPlansSoftLaunchFeatures: false,
      showRegPreCarrotPlansRoutingFlowFeatures: false
    }
    if (userInfo.registrationCohort >= 1) {
      cohort.showRegPostCarrotPlansSoftLaunchFeatures = true
    }
    if (userInfo.registrationCohort >= 2) {
      // only those who register as cohort 1 will not see features wrapped by showRegPostCarrotPlansRoutingFlowFeatures
      cohort.showRegPostCarrotPlansRoutingFlowFeatures = true
    }
    if (userInfo.registrationCohort < 1) {
      cohort.showRegPreCarrotPlansSoftLaunchFeatures = true
    }
    if (userInfo.registrationCohort < 2) {
      cohort.showRegPreCarrotPlansRoutingFlowFeatures = true
    }
    return cohort
  }

  return (
    <UserContext.Provider
      value={{
        refreshUserInfo,
        updateUserInfo: (...args) => dispatch(updateUserInfo(...args)),
        company,
        benefitStarted,
        getFirstName,
        ...getRegistrationCohort(),
        ...userInfo
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
